import type { PromoCodeRedeemResponse } from '@spanel/shared-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { SpinWheelCanvasHandle } from '../components/spin-wheel/SpinWheelCanvas';
import { buildDismissKey } from '../components/spin-wheel/spinWheelShared';
import { t } from '../i18n/translator';
import { queryKeys } from '../lib/queryClient';
import {
  spinWheelService,
  type SpinWheelSegment,
  type SpinWheelSpinResult,
  type SpinWheelStatus,
  type SpinWheelWildRedeemResult,
  type SpinWheelWildStatus,
} from '../services/spinWheel.service';
import { getCustomerErrorMessage } from '../utils/customerErrorMessage';

import { useAuth } from './AuthContext';
import { useSiteSettings } from './SiteSettingsContext';

export type SpinWheelSurface = {
  userKey: string;
  dismissKey: string;
  status: SpinWheelStatus;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openSpinModal: () => void;
  handleClose: () => void;
  code: string;
  setCode: React.Dispatch<React.SetStateAction<string>>;
  uiError: string | null;
  uiSuccess: string | null;
  setUiError: React.Dispatch<React.SetStateAction<string | null>>;
  setUiSuccess: React.Dispatch<React.SetStateAction<string | null>>;
  lastWonSegment: SpinWheelSegment | null;
  isSpinAnimating: boolean;
  spinMutation: UseMutationResult<SpinWheelSpinResult, unknown, void>;
  redeemMutation: UseMutationResult<PromoCodeRedeemResponse, unknown, string>;
  invalidateSpinData: () => Promise<void>;
  handleCodeSubmit: () => void;
  wheelRef: React.RefObject<SpinWheelCanvasHandle | null>;
  wildStatus: SpinWheelWildStatus | null;
  wildLoading: boolean;
  wildRedeemMutation: UseMutationResult<SpinWheelWildRedeemResult, unknown, string>;
  refreshWild: () => Promise<void>;
  handleSpinSettled: () => void;
};

const SpinWheelContext = createContext<SpinWheelSurface | null>(null);

const SPIN_RESULT_REVEAL_DELAY_MS = 1500;

const SPIN_WHEEL_BUSINESS_ERRORS = [
  'Çark kampanyası aktif değil',
  'Aktif çark segmenti bulunamadı',
  'Kullanılabilir çark hakkınız yok',
  'Geçersiz promosyon kodu',
  'Bu promosyon kodunu zaten kullandınız',
  'Wild ödülü bulunamadı veya pasif durumda',
  'Bu Wild ödülünün stoğu tükenmiş',
  'Wild bakiyeniz yetersiz',
] as const;

type PendingSpinOutcome = {
  message: string | null;
  segment: SpinWheelSegment | null;
};

export function useSpinWheel(): SpinWheelSurface | null {
  return useContext(SpinWheelContext);
}

export const SpinWheelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuth();
  const { settings } = useSiteSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [code, setCode] = useState('');
  const [uiError, setUiError] = useState<string | null>(null);
  const [uiSuccess, setUiSuccess] = useState<string | null>(null);
  const [lastWonSegment, setLastWonSegment] = useState<SpinWheelSegment | null>(null);
  const [isSpinAnimating, setIsSpinAnimating] = useState(false);
  const wheelRef = useRef<SpinWheelCanvasHandle | null>(null);
  const pendingSpinOutcomeRef = useRef<PendingSpinOutcome | null>(null);
  const revealSpinOutcomeTimerRef = useRef<number | null>(null);

  const userKey = String(user?.customer_id ?? 'guest');
  const siteId = settings.site_id;
  const customerId = user?.customer_id;
  const dismissKey = buildDismissKey(user?.customer_id);

  const statusQuery = useQuery({
    queryKey: queryKeys.spin.status(siteId, customerId),
    queryFn: () => spinWheelService.getStatus(),
    enabled: isAuthenticated,
    staleTime: 10_000,
  });

  const status = statusQuery.isError ? undefined : statusQuery.data;

  const hasWildSegment = useMemo(
    () => Boolean(status?.segments?.some((segment) => segment.rewardType === 'wild')),
    [status?.segments]
  );

  const wildStatusQuery = useQuery({
    queryKey: queryKeys.spin.wild(siteId, customerId),
    queryFn: () => spinWheelService.getWildStatus(),
    enabled: isAuthenticated && Boolean(status?.enabled) && hasWildSegment,
    staleTime: 10_000,
  });

  const wildStatus =
    statusQuery.isError || wildStatusQuery.isError
      ? null
      : wildStatusQuery.data ?? null;

  const invalidateSpinData = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.spin.status(siteId, customerId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.spin.wild(siteId, customerId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.list(siteId, customerId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.bonus.all(siteId),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.bonus.active(
          siteId,
          customerId,
        ),
      }),
    ]);
  }, [customerId, queryClient, siteId]);

  const commitPendingSpinOutcome = useCallback(() => {
    const pending = pendingSpinOutcomeRef.current;
    if (!pending) {
      return;
    }
    pendingSpinOutcomeRef.current = null;
    if (revealSpinOutcomeTimerRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(revealSpinOutcomeTimerRef.current);
      revealSpinOutcomeTimerRef.current = null;
    }
    setIsSpinAnimating(false);
    setLastWonSegment(pending.segment);
    setUiSuccess(pending.message);
    void invalidateSpinData();
  }, [invalidateSpinData]);

  const clearPendingSpinOutcome = useCallback(() => {
    pendingSpinOutcomeRef.current = null;
    if (revealSpinOutcomeTimerRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(revealSpinOutcomeTimerRef.current);
      revealSpinOutcomeTimerRef.current = null;
    }
  }, []);

  const refreshWild = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.spin.wild(siteId, customerId),
    });
  }, [customerId, queryClient, siteId]);

  const wildRedeemMutation = useMutation<SpinWheelWildRedeemResult, unknown, string>({
    mutationFn: (rewardId: string) => {
      const idempotencyKey =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `wild-redeem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return spinWheelService.redeemWild(rewardId, idempotencyKey);
    },
    onSuccess: async (result) => {
      clearPendingSpinOutcome();
      setUiError(null);
      setUiSuccess(result.message || t(2640));
      await refreshWild();
      await queryClient.invalidateQueries({
        queryKey: queryKeys.bonus.all(settings.site_id),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.bonus.active(
          settings.site_id,
          user?.customer_id,
        ),
      });
    },
    onError: (error: unknown) => {
      setUiError(
        getCustomerErrorMessage(error, {
          fallback: t(2213),
          logLabel: 'SpinWheel.wildRedeem',
          allowMessages: SPIN_WHEEL_BUSINESS_ERRORS,
        }),
      );
    },
  });

  useEffect(() => {
    if (!status?.enabled) {
      setIsOpen(false);
      return;
    }

    if (!status.popupAutoOpen || status.availableCredits <= 0) {
      return;
    }

    if (typeof window !== 'undefined' && window.sessionStorage.getItem(dismissKey) === '1') {
      return;
    }

    setIsOpen(true);
  }, [dismissKey, status?.availableCredits, status?.enabled, status?.popupAutoOpen]);

  const redeemMutation = useMutation({
    mutationFn: (promoCode: string) => spinWheelService.redeemCode(promoCode),
    onSuccess: async (result) => {
      // Redeem akışı alt banner'ı kullanır. Önceki spin sonucunun üst kutusunu
      // temizleyelim ki "Kazanılan: X" üstte, "Kod başarıyla kullanıldı" altta
      // aynı anda görünüp kafa karıştırmasın.
      clearPendingSpinOutcome();
      setUiError(null);
      setLastWonSegment(null);
      setUiSuccess(
        typeof result.message === 'string' && result.message
          ? result.message
          : t(2641)
      );
      setCode('');
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(dismissKey);
      }
      await invalidateSpinData();
      setIsOpen(true);
    },
    onError: (error: unknown) => {
      setUiError(
        getCustomerErrorMessage(error, {
          fallback: t(2213),
          logLabel: 'SpinWheel.redeemCode',
          allowMessages: SPIN_WHEEL_BUSINESS_ERRORS,
        }),
      );
    },
  });

  const spinMutation = useMutation({
    mutationFn: () => {
      // Start the pre-spin animation immediately (wheel spins while API call is in flight)
      clearPendingSpinOutcome();
      setIsSpinAnimating(true);
      setUiSuccess(null);
      setUiError(null);
      setLastWonSegment(null);
      wheelRef.current?.startPreSpin();
      return spinWheelService.spin();
    },
    onSuccess: (result) => {
      const segment = result.segment;
      let successMessage = result.message || null;

      if (segment) {
        const isWin = segment.bonusId > 0 || segment.rewardType === 'physical' || segment.rewardType === 'wild';
        const prefix =
          segment.rewardType === 'physical'
            ? t(2642)
            : isWin
              ? t(2643)
              : t(2644);
        successMessage = `${prefix} ${segment.label}`;
        pendingSpinOutcomeRef.current = {
          message: successMessage,
          segment,
        };
        wheelRef.current?.finishSpin(segment.id, isWin);
      } else {
        pendingSpinOutcomeRef.current = {
          message: successMessage,
          segment: null,
        };
        wheelRef.current?.cancelSpin();
        if (typeof window !== 'undefined') {
          revealSpinOutcomeTimerRef.current = window.setTimeout(() => {
            commitPendingSpinOutcome();
          }, SPIN_RESULT_REVEAL_DELAY_MS);
        }
      }
    },
    onError: (error: unknown) => {
      clearPendingSpinOutcome();
      setIsSpinAnimating(false);
      wheelRef.current?.cancelSpin();
      setUiError(
        getCustomerErrorMessage(error, {
          fallback: t(2213),
          logLabel: 'SpinWheel.spin',
          allowMessages: SPIN_WHEEL_BUSINESS_ERRORS,
        }),
      );
    },
  });

  const handleSpinSettled = useCallback(() => {
    commitPendingSpinOutcome();
  }, [commitPendingSpinOutcome]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(dismissKey, '1');
    }
  }, [dismissKey]);

  const openSpinModal = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(dismissKey);
    }
    setIsOpen(true);
  }, [dismissKey]);

  const handleCodeSubmit = useCallback(() => {
    const normalized = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!normalized) {
      setUiError('Promosyon kodu gerekli');
      return;
    }
    redeemMutation.mutate(normalized);
  }, [code, redeemMutation]);

  const value = useMemo<SpinWheelSurface | null>(() => {
    if (!isAuthenticated || !status?.enabled) {
      return null;
    }

    return {
      userKey,
      dismissKey,
      status,
      isOpen,
      setIsOpen,
      openSpinModal,
      handleClose,
      code,
      setCode,
      uiError,
      uiSuccess,
      setUiError,
      setUiSuccess,
      lastWonSegment,
      isSpinAnimating,
      spinMutation,
      redeemMutation,
      invalidateSpinData,
      handleCodeSubmit,
      wheelRef,
      wildStatus,
      wildLoading: wildStatusQuery.isLoading,
      wildRedeemMutation,
      refreshWild,
      handleSpinSettled,
    };
  }, [
    code,
    dismissKey,
    handleSpinSettled,
    handleClose,
    handleCodeSubmit,
    invalidateSpinData,
    isAuthenticated,
    isSpinAnimating,
    isOpen,
    lastWonSegment,
    openSpinModal,
    redeemMutation,
    refreshWild,
    spinMutation,
    status,
    uiError,
    uiSuccess,
    userKey,
    wildRedeemMutation,
    wildStatus,
    wildStatusQuery.isLoading,
  ]);

  useEffect(() => {
    return () => {
      clearPendingSpinOutcome();
    };
  }, [clearPendingSpinOutcome]);

  return <SpinWheelContext.Provider value={value}>{children}</SpinWheelContext.Provider>;
};
